/**
 * Missiles in flight: proportional navigation, a motor that burns out, and a fuse.
 *
 * ## Proportional navigation, and why it is the only guidance law worth writing
 *
 * The naïve idea — point the missile at the target and keep pointing at it — is *pursuit*
 * guidance, and it is terrible. Against anything that is moving across your path it curves in
 * behind the target and arrives in a tail chase with no energy left, which is why it was
 * abandoned in about 1950.
 *
 * What actually works is the observation a sailor makes without thinking: **if the bearing to
 * another vessel is not changing, you are going to collide.** So instead of steering at the
 * target, steer to *stop the line of sight rotating*:
 *
 * ```
 *   a_cmd = N · V_c · ω_LOS × û_LOS
 * ```
 *
 * where `ω_LOS` is the angular rate of the line of sight, `V_c` the closing speed, and `N` the
 * navigation constant — 3 to 5, 4 here. Against a non-manoeuvring target this produces a
 * dead-straight collision course and the missile never turns at all. Against a manoeuvring one it
 * turns exactly as hard as it needs to.
 *
 * ## Which is also exactly why a hard, well-timed break defeats it
 *
 * The command scales with `V_c`, the *closing* speed, and with the line-of-sight rate. Break early
 * and gently and the LOS rate is small, the missile trims out the change for almost nothing and
 * still has energy. Break late and hard, across the missile's path, and the geometry demands a
 * lateral acceleration that grows without bound as the range closes — and the missile has
 * {@link MissileConfig.maxLateralG} and no more. It saturates, the miss distance opens past the
 * fuse radius, and it goes by.
 *
 * So the acceptance criterion — "tracks the jinking drone but is evadable with a hard, well-timed
 * break" — is not a difficulty knob anywhere in this file. It is what pro-nav *does*, and the
 * numbers that set where the line falls are the g limit and the fuse radius.
 *
 * ## Two more things that make it a missile rather than a homing dart
 *
 * **The motor burns out.** A missile is a glider for most of its flight; it is fast because it
 * *was* accelerated, and every degree it turns costs it energy it cannot get back. A long
 * stern chase after a burnt-out motor fails on speed alone.
 *
 * **The seeker is on gimbals.** The head can look {@link MissileConfig.seekerGimbalHalfAngleDeg}
 * off the missile's own axis and no further. A target that generates enough crossing rate slides
 * out of the field of view, and the missile goes ballistic — the other way a break defeats it,
 * and the reason the gimbal limit must exceed the launch cone (see the validator).
 *
 * `updateFixed(dt)`: yes — {@link MissileBattery.updateFixed}. Allocation-free after construction.
 */

import { Vector3 } from 'three';
import { DEG2RAD, G0 } from '../../core/math.js';
import {
  SEA_LEVEL_DENSITY_KG_PER_M3,
  integrateBallistic,
  inverseBallisticCoefficient,
  segmentSphereHit,
} from './ballistics.js';
import type { MissileConfig } from './config.js';
import type { HitQueue, TargetList } from './engagement.js';

/** Why a missile stopped existing. Drives which effect the renderer plays. */
export type MissileEnd = 'none' | 'hit' | 'proximity' | 'timeout' | 'ground';

/** One round in flight. A plain struct — see `engagement.ts` on serialisable state. */
export interface MissileRound {
  active: boolean;
  readonly positionNed: Vector3;
  readonly previousNed: Vector3;
  readonly velocityNed: Vector3;
  /** Unit vector along the body axis — the renderer points the model with it. */
  readonly axisNed: Vector3;
  ageS: number;
  /** Seconds of boost left. Zero means coasting. */
  motorTimeS: number;
  /** Id of the target it was launched at. */
  targetId: number;
  /** Still homing? False once the seeker has lost the target; it coasts on ballistically. */
  guiding: boolean;
  /** Current mass [kg] — falls as the motor burns. */
  massKg: number;
  /**
   * Lateral acceleration the airframe is *actually* producing [m/s²], as opposed to what the
   * guidance law asked for. Lags the command by {@link MissileConfig.autopilotLagS}.
   */
  readonly achievedLateralNed: Vector3;
}

function makeMissile(): MissileRound {
  return {
    active: false,
    positionNed: new Vector3(),
    previousNed: new Vector3(),
    velocityNed: new Vector3(),
    axisNed: new Vector3(1, 0, 0),
    ageS: 0,
    motorTimeS: 0,
    targetId: -1,
    guiding: false,
    massKg: 0,
    achievedLateralNed: new Vector3(),
  };
}

/** A detonation, for the effects layer to turn into a fireball. */
export interface MissileDetonation {
  readonly positionNed: Vector3;
  reason: MissileEnd;
  /** −1 when it detonated on nothing. */
  targetId: number;
}

const toTarget = /* #__PURE__ */ new Vector3();
const relativeVelocity = /* #__PURE__ */ new Vector3();
const losRate = /* #__PURE__ */ new Vector3();
const command = /* #__PURE__ */ new Vector3();
const acceleration = /* #__PURE__ */ new Vector3();

/**
 * Every missile the aircraft carries, plus the ones in the air.
 *
 * One object rather than a `Missile` class per round because the rounds are pooled and the
 * interesting state — how many are left on the rails — belongs to the aircraft, not to any round.
 */
export class MissileBattery {
  readonly config: MissileConfig;
  /** The pool. Walk it and skip `!active`. */
  readonly rounds: MissileRound[] = [];
  /** Detonations from the last tick, for the effects layer. Cleared by {@link clearEvents}. */
  readonly detonations: MissileDetonation[] = [];

  private readonly inverseBc: number;
  private readonly cosGimbal: number;
  private readonly maxLateralMps2: number;
  /** `ρ₀·v_ref²` — the denominator of the dynamic-pressure ratio, with the ½ cancelled out. */
  private readonly referenceQ2: number;
  private remainingValue: number;
  private detonationCount = 0;
  private firedValue = 0;

  constructor(config: MissileConfig) {
    this.config = config;
    this.remainingValue = config.count;
    this.inverseBc = inverseBallisticCoefficient(
      config.diameterM,
      config.massKg,
      config.dragCoefficient,
    );
    this.cosGimbal = Math.cos(config.seekerGimbalHalfAngleDeg * DEG2RAD);
    this.maxLateralMps2 = config.maxLateralG * G0;
    this.referenceQ2 =
      SEA_LEVEL_DENSITY_KG_PER_M3 *
      config.manoeuvreReferenceSpeedMps *
      config.manoeuvreReferenceSpeedMps;

    // Every round carried can be in the air at once, and no more.
    for (let i = 0; i < config.count; i++) {
      this.rounds.push(makeMissile());
      this.detonations.push({ positionNed: new Vector3(), reason: 'none', targetId: -1 });
    }
  }

  /** Rounds still on the rails. */
  get remaining(): number {
    return this.remainingValue;
  }

  get fired(): number {
    return this.firedValue;
  }

  /** How many detonations happened last tick. Index {@link detonations} up to this. */
  get detonationLength(): number {
    return this.detonationCount;
  }

  clearEvents(): void {
    this.detonationCount = 0;
  }

  /** Reload the rails. The pause menu's REARM. */
  rearm(): void {
    this.remainingValue = this.config.count;
  }

  /**
   * Carry every missile in flight through a floating-origin rebase.
   *
   * Same reasoning as `CannonSystem.applyOriginShift`, and more urgent: a missile lives up to
   * twenty-five seconds and its target re-derives from a geodetic anchor every tick, so at a 1.9 s
   * rebase period a single missile now crosses a dozen rebases between launch and impact. Left
   * uncarried it would accumulate the whole flight's worth of shifts as guidance error — the seeker
   * would chase a target that appears to jump 500 m sideways every two seconds and the round would
   * fly a slalom, which is a bug that looks like bad guidance rather than like a frame problem.
   *
   * `previousNed` moves too, for the swept proximity test; `velocityNed`, `axisNed` and
   * `achievedLateralNed` do not, being a rate, a direction and an acceleration respectively.
   */
  applyOriginShift(deltaNorthM: number, deltaEastM: number, deltaDownM: number): void {
    for (const round of this.rounds) {
      if (!round.active) continue;
      round.positionNed.x += deltaNorthM;
      round.positionNed.y += deltaEastM;
      round.positionNed.z += deltaDownM;
      round.previousNed.x += deltaNorthM;
      round.previousNed.y += deltaEastM;
      round.previousNed.z += deltaDownM;
    }
  }

  /** Clear rounds in the air without touching the stores count. */
  clear(): void {
    for (const round of this.rounds) round.active = false;
    this.detonationCount = 0;
  }

  /** Full reset: rails reloaded, air cleared, counters zeroed. */
  reset(): void {
    this.clear();
    this.remainingValue = this.config.count;
    this.firedValue = 0;
  }

  /**
   * Send one, if there is one to send.
   *
   * @param originNed  Rail position, sim-frame NED [m].
   * @param velocityNed The launch aircraft's velocity, which the missile keeps [m/s].
   * @param axisNed    Unit vector the missile leaves along — the aircraft's nose.
   * @param targetId   What the seeker had locked.
   * @returns the round, or null when the rails are empty or the pool is busy.
   */
  launch(
    originNed: Vector3,
    velocityNed: Vector3,
    axisNed: Vector3,
    targetId: number,
  ): MissileRound | null {
    if (this.remainingValue <= 0) return null;
    let round: MissileRound | null = null;
    for (let i = 0; i < this.rounds.length; i++) {
      if (this.rounds[i].active) continue;
      round = this.rounds[i];
      break;
    }
    if (round === null) return null;

    this.remainingValue--;
    this.firedValue++;
    round.active = true;
    round.ageS = 0;
    round.motorTimeS = this.config.motorBurnS;
    round.targetId = targetId;
    round.guiding = targetId >= 0;
    round.massKg = this.config.massKg;
    round.achievedLateralNed.set(0, 0, 0);
    round.positionNed.copy(originNed);
    round.previousNed.copy(originNed);
    round.axisNed.copy(axisNed);
    /*
     * It leaves with the aeroplane's velocity, not at rest and not at some launch speed.
     *
     * A rail-launched missile is simply released; for the first fraction of a second it is doing
     * exactly what the aircraft was doing, and everything after that is its own motor. Launching
     * it at a nominal speed along the nose instead would make a missile fired in a hard turn fly
     * off in a direction the aeroplane was never pointing.
     */
    round.velocityNed.copy(velocityNed);
    return round;
  }

  /**
   * Fly every round one step.
   *
   * @param densityKgPerM3 Air density [kg/m³]. One figure for all rounds: they are within a few
   *   hundred metres of each other and of the aircraft, where the density difference is under 3 %.
   * @param groundElevationM Ground below the aircraft [m]; see the same note in `cannon.ts`.
   */
  updateFixed(
    dt: number,
    targets: TargetList,
    hits: HitQueue,
    densityKgPerM3: number,
    groundElevationM: number,
  ): void {
    const config = this.config;
    const groundZ = -groundElevationM;

    for (let i = 0; i < this.rounds.length; i++) {
      const round = this.rounds[i];
      if (!round.active) continue;

      round.previousNed.copy(round.positionNed);
      round.ageS += dt;

      const target = round.guiding ? findTarget(targets, round.targetId) : null;
      if (round.guiding && target === null) round.guiding = false;

      // --- guidance ------------------------------------------------------------------------
      command.set(0, 0, 0);
      if (target !== null) {
        toTarget.subVectors(target.positionNed, round.positionNed);
        const range = toTarget.length();
        const speed = round.velocityNed.length();

        if (speed > 1e-3 && range > 1e-3) {
          round.axisNed.copy(round.velocityNed).multiplyScalar(1 / speed);
          // Off-boresight: has the target slid outside what the gimbals can follow?
          if (toTarget.dot(round.axisNed) / range < this.cosGimbal) {
            round.guiding = false;
          } else {
            relativeVelocity.subVectors(target.velocityNed, round.velocityNed);
            /*
             * ω_LOS = (r × v_rel) / r·r — the rotation rate of the sight line, as a vector along
             * the axis it turns about. This is the quantity the whole law is built on, and the
             * division by `r·r` rather than by `|r|` is not a normalisation slip: it is what makes
             * the result an angular rate rather than a velocity.
             */
            losRate.crossVectors(toTarget, relativeVelocity).multiplyScalar(1 / (range * range));
            const closingSpeed = -relativeVelocity.dot(toTarget) / range;

            // a = N · V_c · (ω × û). Cross first, then scale — the cross of two scratch vectors
            // must not alias its own inputs.
            command.crossVectors(losRate, toTarget).multiplyScalar(
              (config.navigationConstant * closingSpeed) / range,
            );

            /*
             * Strip the along-axis component.
             *
             * Pro-nav's command is an acceleration in free space; a missile can only produce it
             * with fins, and fins produce force *across* the airflow. Leaving the along-axis part
             * in would let the guidance law add or remove speed at will — a missile that
             * accelerates to catch up, which is not a thing.
             */
            const along = command.dot(round.axisNed);
            command.addScaledVector(round.axisNed, -along);

            /*
             * What the airframe can actually deliver here, now.
             *
             * Turning is lift and lift is `C_L·q̄·S`, so the ceiling scales with dynamic pressure
             * against the reference condition. A missile that launched at 30 g capability and has
             * since burned out and bled speed in a turn is a much less dangerous object, which is
             * both true and the thing that makes the endgame a contest rather than a formality.
             */
            const speedNow = round.velocityNed.length();
            const available =
              this.maxLateralMps2 *
              Math.min(1, (densityKgPerM3 * speedNow * speedNow) / this.referenceQ2);
            const demand = command.length();
            if (demand > available) {
              // Saturated: this is the moment a well-timed break wins.
              command.multiplyScalar(available / demand);
            }
          }
        }
      }

      /*
       * The autopilot lag.
       *
       * `command` is what the guidance law wants; `achievedLateralNed` is what the fins have got
       * round to producing. A first-order lag between them is the difference between a missile
       * that can be out-turned at the last second and one that cannot — during the quarter second
       * it takes to respond, a breaking target is already going somewhere else.
       *
       * Exponential rather than linear so the response is frame-rate independent.
       */
      const blend = 1 - Math.exp(-dt / config.autopilotLagS);
      round.achievedLateralNed.lerp(command, blend);

      // --- motor, drag, gravity -------------------------------------------------------------
      acceleration.copy(round.achievedLateralNed);
      if (round.motorTimeS > 0) {
        const burn = Math.min(dt, round.motorTimeS);
        round.motorTimeS -= burn;
        // Mass falls linearly with the propellant burned.
        const burnedFraction = 1 - round.motorTimeS / config.motorBurnS;
        round.massKg = config.massKg - config.propellantMassKg * burnedFraction;
        const speed = round.velocityNed.length();
        if (speed > 1e-3) {
          round.axisNed.copy(round.velocityNed).multiplyScalar(1 / speed);
        }
        // Thrust along the body axis, scaled by how much of the step the motor was lit for.
        acceleration.addScaledVector(
          round.axisNed,
          (config.motorThrustN / round.massKg) * (burn / dt),
        );
      }

      round.velocityNed.addScaledVector(acceleration, dt);
      /*
       * Drag and gravity come from the shared integrator, so a missile and a cannon round obey
       * the same air. The inverse ballistic coefficient is computed from the *launch* mass and
       * held: recomputing it as the propellant burns would make the round get draggier as it got
       * lighter, which is backwards.
       */
      integrateBallistic(
        round.positionNed,
        round.velocityNed,
        this.inverseBc,
        densityKgPerM3,
        dt,
      );

      const speed = round.velocityNed.length();
      if (speed > 1e-3) round.axisNed.copy(round.velocityNed).multiplyScalar(1 / speed);

      // --- has it arrived? --------------------------------------------------------------------
      let ended: MissileEnd = 'none';
      let hitTargetId = -1;

      if (round.ageS > 0.15) {
        // A short arming delay, so a missile cannot fuse on its own launch aircraft's wingtip.
        for (let t = 0; t < targets.length; t++) {
          const candidate = targets.at(t);
          if (!candidate.alive) continue;
          const fraction = segmentSphereHit(
            round.previousNed,
            round.positionNed,
            candidate.positionNed,
            candidate.radiusM + config.proximityFuseM,
          );
          if (fraction < 0) continue;

          const report = hits.push();
          if (report !== null) {
            report.targetId = candidate.id;
            report.kind = 'missile';
            report.damage = config.warheadDamage;
            report.positionNed.lerpVectors(round.previousNed, round.positionNed, fraction);
            report.speedMps = speed;
            report.directionNed.copy(round.axisNed);
          }
          hitTargetId = candidate.id;
          ended = candidate.id === round.targetId ? 'hit' : 'proximity';
          break;
        }
      }

      if (ended === 'none') {
        if (round.positionNed.z >= groundZ) ended = 'ground';
        else if (round.ageS >= config.lifetimeS) ended = 'timeout';
      }

      if (ended !== 'none') {
        this.recordDetonation(round, ended, hitTargetId);
        round.active = false;
      }
    }
  }

  private recordDetonation(round: MissileRound, reason: MissileEnd, targetId: number): void {
    if (this.detonationCount >= this.detonations.length) return;
    const event = this.detonations[this.detonationCount++];
    event.positionNed.copy(round.positionNed);
    event.reason = reason;
    event.targetId = targetId;
  }
}

/**
 * The tracked target, by id, or null if it is gone.
 *
 * By id rather than by a cached index because the mode is free to reorder its target array
 * between ticks, and a stale index is how a missile ends up guiding on whatever moved into slot 3.
 */
function findTarget(targets: TargetList, id: number): ReturnType<TargetList['at']> | null {
  if (id < 0) return null;
  for (let i = 0; i < targets.length; i++) {
    const target = targets.at(i);
    if (target.id === id) return target.alive ? target : null;
  }
  return null;
}
